/**
 * Small, dependency-free geometry helpers for screening providers.
 *
 * Distances use a local equirectangular projection. At the five-mile search
 * radius used by MRDS and NRHP this is materially more accurate than the
 * source geometries themselves, while keeping provider code deterministic in
 * browser, Cloud Run, and Vitest runtimes.
 */
export interface GeoPoint {
  lat: number;
  lng: number;
}

const METRES_PER_DEG_LAT = 111_132;
const METRES_PER_MILE = 1_609.344;

function toLocalMetres(point: GeoPoint, origin: GeoPoint): { x: number; y: number } {
  const cosLat = Math.max(0.01, Math.cos(origin.lat * Math.PI / 180));
  return {
    x: (point.lng - origin.lng) * METRES_PER_DEG_LAT * cosLat,
    y: (point.lat - origin.lat) * METRES_PER_DEG_LAT,
  };
}

/** Even/odd polygon test. The boundary is considered inside. */
export function pointInPolygon(point: GeoPoint, polygon: readonly GeoPoint[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    const cross = (point.lng - a.lng) * (b.lat - a.lat) -
      (point.lat - a.lat) * (b.lng - a.lng);
    const onSegment = Math.abs(cross) < 1e-12 &&
      point.lng >= Math.min(a.lng, b.lng) - 1e-12 &&
      point.lng <= Math.max(a.lng, b.lng) + 1e-12 &&
      point.lat >= Math.min(a.lat, b.lat) - 1e-12 &&
      point.lat <= Math.max(a.lat, b.lat) + 1e-12;
    if (onSegment) return true;
    const intersects = ((a.lat > point.lat) !== (b.lat > point.lat)) &&
      point.lng < (b.lng - a.lng) * (point.lat - a.lat) / (b.lat - a.lat) + a.lng;
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointToSegmentMetres(point: GeoPoint, a: GeoPoint, b: GeoPoint): number {
  const p0 = toLocalMetres(point, point);
  const p1 = toLocalMetres(a, point);
  const p2 = toLocalMetres(b, point);
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const denom = dx * dx + dy * dy;
  if (denom === 0) return Math.hypot(p1.x - p0.x, p1.y - p0.y);
  const t = Math.max(0, Math.min(1, ((p0.x - p1.x) * dx + (p0.y - p1.y) * dy) / denom));
  return Math.hypot(p0.x - (p1.x + t * dx), p0.y - (p1.y + t * dy));
}

/** Minimum point-to-parcel distance. A point on or inside is zero. */
export function pointToPolygonDistanceMiles(point: GeoPoint, polygon: readonly GeoPoint[]): number {
  if (polygon.length < 3) return haversineMiles(point, polygon[0] ?? point);
  if (pointInPolygon(point, polygon)) return 0;
  let best = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    best = Math.min(best, pointToSegmentMetres(point, polygon[i]!, polygon[(i + 1) % polygon.length]!));
  }
  return best / METRES_PER_MILE;
}

function orientation(a: GeoPoint, b: GeoPoint, c: GeoPoint): number {
  return (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
}

function segmentsIntersect(a: GeoPoint, b: GeoPoint, c: GeoPoint, d: GeoPoint): boolean {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);
  if (((o1 > 0 && o2 < 0) || (o1 < 0 && o2 > 0)) &&
      ((o3 > 0 && o4 < 0) || (o3 < 0 && o4 > 0))) return true;
  const eps = 1e-12;
  const on = (p: GeoPoint, q: GeoPoint, r: GeoPoint) =>
    q.lng >= Math.min(p.lng, r.lng) - eps && q.lng <= Math.max(p.lng, r.lng) + eps &&
    q.lat >= Math.min(p.lat, r.lat) - eps && q.lat <= Math.max(p.lat, r.lat) + eps;
  return (Math.abs(o1) <= eps && on(a, c, b)) ||
    (Math.abs(o2) <= eps && on(a, d, b)) ||
    (Math.abs(o3) <= eps && on(c, a, d)) ||
    (Math.abs(o4) <= eps && on(c, b, d));
}

/** Minimum polygon-to-polygon distance. Intersections and containment are zero. */
export function polygonDistanceMiles(a: readonly GeoPoint[], b: readonly GeoPoint[]): number {
  if (a.length < 3 || b.length < 3) return Infinity;
  if (pointInPolygon(a[0]!, b) || pointInPolygon(b[0]!, a)) return 0;
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (segmentsIntersect(a[i]!, a[(i + 1) % a.length]!, b[j]!, b[(j + 1) % b.length]!)) return 0;
    }
  }
  let best = Infinity;
  for (const p of a) best = Math.min(best, pointToPolygonDistanceMiles(p, b));
  for (const p of b) best = Math.min(best, pointToPolygonDistanceMiles(p, a));
  return best;
}

export function haversineMiles(a: GeoPoint, b: GeoPoint): number {
  const toRad = (v: number) => v * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 3_958.7613 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function searchEnvelope(
  centre: GeoPoint,
  boundary: readonly GeoPoint[] | undefined,
  radiusMiles: number,
): { minLat: number; minLng: number; maxLat: number; maxLng: number } {
  const points = boundary && boundary.length >= 3 ? boundary : [centre];
  const minLat0 = Math.min(...points.map(p => p.lat));
  const maxLat0 = Math.max(...points.map(p => p.lat));
  const minLng0 = Math.min(...points.map(p => p.lng));
  const maxLng0 = Math.max(...points.map(p => p.lng));
  const latPad = radiusMiles / 69;
  const lngPad = radiusMiles / (69 * Math.max(0.1, Math.cos(centre.lat * Math.PI / 180)));
  return {
    minLat: minLat0 - latPad,
    minLng: minLng0 - lngPad,
    maxLat: maxLat0 + latPad,
    maxLng: maxLng0 + lngPad,
  };
}

/** Convert an ArcGIS `[x, y]` ring to our latitude/longitude shape. */
export function arcGisRingToPoints(ring: readonly (readonly number[])[]): GeoPoint[] {
  return ring
    .filter(p => p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map(p => ({ lng: p[0]!, lat: p[1]! }));
}
